import { Injectable, computed, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { hasAdminRole } from '../helper/role.util';

/**
 * Cached-Store für die effektiven Berechtigungen des angemeldeten Benutzers.
 *
 * - Lädt nach Login `GET /v1/permission/my` und cached die `identifier`-Werte.
 * - Leert den Cache bei Logout.
 * - Lädt automatisch neu, wenn sich der effektive Benutzer ändert (z. B. Impersonation).
 * - `isSystemAdmin`-Benutzer und Benutzer mit der Administrator-Rolle überspringen alle Checks
 *   (analog zum `AdminGuard` und zum serverseitigen RolesGuard).
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
    private readonly _identifiers = signal<Set<string>>(new Set<string>());
    /** Reaktives Set der Berechtigungs-Identifier (für Direktive/Menü). */
    readonly identifiers = this._identifiers.asReadonly();

    private readonly _isSystemAdmin = signal<boolean>(false);
    readonly isSystemAdmin = this._isSystemAdmin.asReadonly();

    /** `true`, wenn der Benutzer die Administrator-Rolle besitzt – überspringt (wie System-Admin) alle Checks. */
    private readonly _isAdminRole = signal<boolean>(false);
    readonly isAdminRole = this._isAdminRole.asReadonly();

    /** Monotone Revision, die bei jeder Änderung steigt – als `effect()`-Trigger. */
    private readonly _revision = signal<number>(0);
    readonly version = computed(() => this._revision());

    /** Wird `true`, sobald die Berechtigungen für die aktuelle Session geladen wurden (für Guards). */
    private readonly _ready$ = new BehaviorSubject<boolean>(false);
    readonly ready$ = this._ready$.asObservable();

    private loadedForUserId: string | undefined;

    constructor(
        private apiService: ApiService,
        private authService: AuthService
    ) {
        this.authService.sessionData.subscribe((session) => {
            const loggedIn = session?.isLoggedIn && !!session.token;
            const wasAdmin = this._isSystemAdmin();
            const wasAdminRole = this._isAdminRole();
            this._isSystemAdmin.set(!!session?.user?.isSystemAdmin);
            this._isAdminRole.set(hasAdminRole(session?.user?.roles));
            if (wasAdmin !== this._isSystemAdmin() || wasAdminRole !== this._isAdminRole()) {
                this._revision.update((r) => r + 1);
            }

            if (!loggedIn) {
                this.clear();
                return;
            }

            const userId = session.user?._id;
            // Bei Login oder Benutzerwechsel (Impersonation) neu laden.
            if (userId && userId !== this.loadedForUserId) {
                this.loadedForUserId = userId;
                this.loadMyPermissions();
            }
        });
    }

    loadMyPermissions(): void {
        this._ready$.next(false);
        this.apiService.getMyPermissions().subscribe({
            next: (permissions) => {
                this._identifiers.set(new Set((permissions ?? []).map((p) => p.identifier)));
                this._revision.update((r) => r + 1);
                this._ready$.next(true);
            },
            error: (error) => {
                console.error('Failed to load effective permissions:', error);
                this._identifiers.set(new Set<string>());
                this._revision.update((r) => r + 1);
                this._ready$.next(true);
            }
        });
    }

    clear(): void {
        this.loadedForUserId = undefined;
        this._identifiers.set(new Set<string>());
        this._revision.update((r) => r + 1);
        this._ready$.next(false);
    }

    /**
     * Prüft, ob der Benutzer eine (oder bei Array: mindestens eine – OR-Semantik)
     * der Berechtigungen besitzt. System-Admins bestehen jeden Check.
     */
    hasPermission(identifier: string | string[]): boolean {
        if (this._isSystemAdmin() || this._isAdminRole()) return true;
        const required = Array.isArray(identifier) ? identifier : [identifier];
        if (required.length === 0) return true;
        const set = this._identifiers();
        return required.some((id) => set.has(id));
    }
}
